import { Dispatch, FC, SetStateAction, useEffect, useState } from "react";
import * as Yup from "yup";
import Modal from "../Modal";
import { Formik, FormikProps } from "formik";
import { IncidenceReasonDTO } from "../../interfaces/Dtos/IncidenceReasonDTO";
import { IncidenceActionDTO } from "../../interfaces/Dtos/IncidenceActionDTO";
import {
  FormSelect,
  FormSimpleRadioGroup,
  FormTextInput,
  MultipleRadioOption,
} from "../FormFields";
import {
  BusinessUnitInterface,
  DeliveryType,
  LocationInterface,
  PieceInterface,
} from "../../interfaces";
import { useAppSelector } from "../../store/hooks";
import {
  getIncidenceActionList,
  getIncidenceList,
  getReasonList,
} from "../../services/incidenceService";
import { MultipleFilterDisclosure } from "../FilterDisclosure";
import { PrimaryButton, SecondaryButton } from "../Buttons";
import { loaderService } from "../../services";
import { AddressSearch, BusinessUnitSearch } from "../LocationSearch";
import { AutocompleteRegion } from "../Form/FormMapsAutocomplete";

export interface IncidenceFormValues {
  incidence: IncidenceReasonDTO | null;
  reason: IncidenceReasonDTO | null;
  action: IncidenceActionDTO | null;
  pieces: MultipleRadioOption<PieceInterface | null>[];
  deliveryType: DeliveryType | null;
  businessUnit: BusinessUnitInterface | null;
  location: LocationInterface | null;
}

interface IncidenceFormProps {
  open: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
  pieces: PieceInterface[];
  formik: FormikProps<IncidenceFormValues>;
}

const NewIncidenceForm: FC<IncidenceFormProps> = ({
  open,
  setOpen,
  pieces,
  formik,
}) => {
  const user = useAppSelector((state) => state.user.user);
  const applicationID = useAppSelector(
    (state) => state.inmutable.appData.applicationID
  );
  const businessUnits = useAppSelector(
    (state) => state.inmutable.businessUnits
  );
  const loading = loaderService.useIsLoading();

  const [incidenceList, setIncidenceList] = useState<IncidenceReasonDTO[]>([]);
  const [reasonList, setReasonList] = useState<IncidenceReasonDTO[]>([]);
  const [incidenceActionList, setIncidenceActionList] = useState<
    IncidenceActionDTO[]
  >([]);

  const getNewIncidencePiece = (
    newValue: MultipleRadioOption<PieceInterface | null>[]
  ) => {
    const oldAllPiecesOption = formik.values.pieces.find(
      (piece) => piece.value === null
    )!;
    const newAllPiecesOption = newValue.find((piece) => piece.value === null)!;

    if (oldAllPiecesOption.checked && !newAllPiecesOption.checked) {
      return newValue.map((piece) => ({ ...piece, checked: false }));
    } else if (!oldAllPiecesOption.checked && newAllPiecesOption.checked) {
      return newValue.map((piece) => ({ ...piece, checked: true }));
    } else {
      const allChecked = newValue.every(
        (piece) => piece.value === null || piece.checked
      );
      return newValue.map((piece) => ({
        ...piece,
        checked: piece.value === null ? allChecked : piece.checked,
      }));
    }
  };

  const handleIncidencePieceChange: Dispatch<
    SetStateAction<MultipleRadioOption<PieceInterface | null>[]>
  > = (newValue) => {
    const updatedPieces = getNewIncidencePiece(
      typeof newValue === "function" ? newValue(formik.values.pieces) : newValue
    );
    formik.setFieldValue("pieces", updatedPieces);
  };

  useEffect(() => {
    if (!pieces) {
      formik.setFieldValue("pieces", []);
      return;
    }

    const incidencePieces: MultipleRadioOption<PieceInterface | null>[] =
      pieces.map((piece) => ({
        name: `Pieza ${piece.pieceNumber}`,
        checked: false,
        value: piece,
      }));
    incidencePieces.push({
      name: "Toda la guía",
      checked: false,
      value: null,
    });
    formik.setFieldValue("pieces", incidencePieces);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pieces]);

  useEffect(() => {
    const profileID = user?.profileID;
    if (!profileID || !applicationID || !open) {
      setIncidenceList([]);
      return;
    }

    const afunction = async () => {
      const response = await getIncidenceList(profileID, applicationID);
      if (!response.didError && response?.model) {
        setIncidenceList(response.model);
      }
    };

    afunction();
  }, [applicationID, user?.profileID, open]);

  useEffect(() => {
    const incidenceID = formik.values.incidence?.incidenceID;
    const profileID = user?.profileID;
    if (!incidenceID || !profileID) {
      setReasonList([]);
      return;
    }

    const afunction = async () => {
      const response = await getReasonList(profileID, incidenceID);
      if (!response.didError && response?.model) {
        setReasonList(response.model);
      }
    };

    afunction();
  }, [formik.values.incidence?.incidenceID, user?.profileID]);

  useEffect(() => {
    const incidenceID = formik.values.incidence?.incidenceID;
    const reasonID = formik.values.reason?.reasonListID;

    if (!incidenceID || !reasonID) {
      setIncidenceActionList([]);
      return;
    }

    const actionfunction = async () => {
      const response = await getIncidenceActionList(
        reasonID,
        incidenceID,
        user?.userLogin
      );
      if (!response.didError && response?.model) {
        setIncidenceActionList(response.model);
      }
    };

    actionfunction();
  }, [
    formik.values.reason?.reasonListID,
    user?.userLogin,
    formik.values.incidence?.incidenceID,
  ]);

  useEffect(() => {
    if (formik.values.action?.requiresAllPieces) {
      formik.setFieldValue(
        "pieces",
        formik.values.pieces.map((piece) => ({ ...piece, checked: true }))
      );
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.values.action]);

  return (
    <form onSubmit={formik.handleSubmit}>
      <div className="flex flex-col gap-4">
        <div>
          <FormSelect
            name="incidence"
            label="Tipo de Incidencia"
            options={incidenceList}
            optionString={(option) => option?.incidenceName ?? ""}
            selected={formik.values.incidence}
            error={
              formik.touched.incidence && formik.errors.incidence
                ? formik.errors.incidence
                : undefined
            }
            onSelectOption={(incidence) => {
              formik.setFieldValue("incidence", incidence);
              formik.setFieldValue("reason", null);
              formik.setFieldValue("action", null);
            }}
          />
        </div>

        <div>
          <FormSelect
            disabled={!formik.values.incidence}
            name="reason"
            label="Motivo/Evidencia"
            options={reasonList}
            optionString={(option) => option?.reasonName ?? ""}
            selected={formik.values.reason}
            error={
              formik.touched.reason && formik.errors.reason
                ? formik.errors.reason
                : undefined
            }
            onSelectOption={(reason) => {
              formik.setFieldValue("reason", reason);
              formik.setFieldValue("action", null);
            }}
          />
        </div>

        <div>
          <FormSelect
            disabled={!formik.values.reason}
            name="action"
            label="Acción"
            options={incidenceActionList}
            optionString={(option) => option?.incidenceActionName ?? ""}
            selected={formik.values.action}
            error={
              formik.touched.action && formik.errors.action
                ? formik.errors.action
                : undefined
            }
            onSelectOption={(action) => {
              formik.setFieldValue("action", action);
            }}
          />
        </div>

        {formik.values.action?.incidenceActionID === 9 && (
          <div>
            <div className="flex justify-between w-full gap-6 mb-2">
              <label className="block text-m font-medium  text-gray-900 truncate">
                Nuevo Destino
              </label>

              <FormSimpleRadioGroup
                horizontal
                name="deliveryType"
                selected={formik.values.deliveryType}
                onSelectOption={(option) => {
                  formik.setFieldValue("deliveryType", option);
                }}
                options={[
                  { value: DeliveryType.AT_OFFICE, name: "Tienda" },
                  { value: DeliveryType.AT_HOME, name: "Domicilio" },
                ]}
              />
            </div>

            {formik.values.deliveryType === DeliveryType.AT_OFFICE && (
              <BusinessUnitSearch
                selected={formik.values.businessUnit ?? undefined}
                setSelectedLocation={(businessUnit) => {
                  formik.setFieldValue("businessUnit", businessUnit);
                }}
              />
            )}

            {formik.values.deliveryType === DeliveryType.AT_HOME && (
              <AddressSearch
                businessUnits={businessUnits}
                selected={formik.values.location ?? undefined}
                region={AutocompleteRegion.TEALCA}
                isInternational={false}
                setSelectedLocation={(location, buConsignee) => {
                  formik.setFieldValue("location", location);
                  formik.setFieldValue("businessUnit", buConsignee);
                }}
              />
            )}
          </div>
        )}

        {formik.values.action?.incidenceActionID === 9 &&
          formik.values.deliveryType === DeliveryType.AT_HOME && (
            <div>
              <FormTextInput
                name="reference"
                label="Punto de Referencia"
                value={formik.values.location?.reference}
                onChange={(value) => {
                  formik.setFieldValue("location", {
                    ...formik.values.location,
                    reference: value.target.value,
                  });
                }}
              />
            </div>
          )}

        <div className="flex">
          <MultipleFilterDisclosure
            showCount
            showInTop
            title="Piezas"
            countDiff={
              formik.values.pieces.find((piece) => piece.value === null)
                ?.checked
                ? -1
                : 0
            }
            options={formik.values.pieces}
            setOptions={handleIncidencePieceChange}
          />
        </div>

        <div className="flex items-center justify-between gap-4 mt-6">
          <SecondaryButton
            className="w-36"
            type="button"
            onClick={() => setOpen(false)}
          >
            Cancelar
          </SecondaryButton>

          <PrimaryButton className="w-36" type="submit" disabled={loading}>
            Agregar
          </PrimaryButton>
        </div>
      </div>
    </form>
  );
};

interface NewIncidenceModalProps {
  open: boolean;
  pieces: PieceInterface[];
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  handleIncidenceSubmit: (values: IncidenceFormValues) => void;
  shipmentNumber: string;
  deliveryType: DeliveryType;
  businessUnit: BusinessUnitInterface;
  location: LocationInterface;
}

export const NewIncidenceModal: FC<NewIncidenceModalProps> = ({
  open,
  pieces,
  setOpen,
  handleIncidenceSubmit,
  shipmentNumber,
  deliveryType,
  businessUnit,
  location,
}) => {
  const validationSchema = Yup.object().shape({
    incidence: Yup.object()
      .nullable()
      .required("El tipo de incidencia es obligatorio"),
    reason: Yup.object()

      .nullable()
      .required("El motivo es obligatorio"),
    action: Yup.object().nullable().required("La acción es obligatoria"),
  });

  return (
    <Modal openModal={open} setOpenModal={setOpen}>
      <div>
        <h2 className="text-lg font-medium text-indigo-600 mb-1">
          Registrar Incidencia
        </h2>
        <p className="mb-8">
          Registrar una nueva incidencia para la guía{" "}
          <strong>{shipmentNumber}</strong> .
        </p>

        <Formik<IncidenceFormValues>
          initialValues={{
            incidence: null,
            reason: null,
            action: null,
            pieces: [],
            deliveryType,
            businessUnit,
            location,
          }}
          onSubmit={(values, formik) => {
            handleIncidenceSubmit(values);
            formik.resetForm();
            formik.setFieldValue(
              "pieces",
              values.pieces.map((piece) => ({ ...piece, checked: false }))
            );
          }}
          validationSchema={validationSchema}
        >
          {(formik) => (
            <NewIncidenceForm
              open={open}
              setOpen={setOpen}
              pieces={pieces}
              formik={formik}
            />
          )}
        </Formik>
      </div>
    </Modal>
  );
};
